import { isNil } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { Weekday } from 'rrule';
import { Batch } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext/BatchContext';
import { i18n } from '@shared/util/i18n';
import { DaysOfWeek, buildCustomRRule, dayOfWeekLabels, getNthWeekdayOfMonth } from '../utils';

interface MenuOption {
  title: string;
  onPress: () => void;
}

interface UseMonthOptionsReturn {
  dateSelectOptions: MenuOption[];
  nthSelectOptions: MenuOption[];
  daySelectOptions: MenuOption[];

  handleDayOfMonthSelect: () => void;
  handleFullNthSelect: () => void;

  isDayOfMonthSelected: boolean;
  isNthWeekdaySelected: boolean;

  dayOfMonthDisplay: string;
  nthDayDisplay: string;
  nthDayWeekdayDisplay: string;
}

export function useMonthOptions({
  rruleOptions,
  setRruleOptions,
}: Pick<Batch, 'rruleOptions' | 'setRruleOptions'>): UseMonthOptionsReturn {
  const isDayOfMonthSelected = !isNil(rruleOptions?.bymonthday);
  const isLastDayOfMonthSelected = isDayOfMonthSelected && rruleOptions?.bymonthday === -1;
  const isNthWeekdaySelected = !isNil(rruleOptions?.byweekday);
  const dstartDate = useMemo(
    () =>
      !isNil(rruleOptions) && !isNil(rruleOptions?.dtstart)
        ? new Date(rruleOptions.dtstart)
        : new Date(),
    [rruleOptions],
  );
  const fallbackDayOfMonthDisplay = dstartDate?.getUTCDate();
  const [dateNumberValue, setDateNumberValue] = useState(
    isDayOfMonthSelected && !isNil(rruleOptions?.bymonthday)
      ? (rruleOptions?.bymonthday as number)
      : fallbackDayOfMonthDisplay,
  );
  const dayOfMonthStr =
    dateNumberValue === -1
      ? i18n('workflowTemplate.component.dateAndRepeat.lastDayOfMonth')
      : moment.localeData().ordinal(dateNumberValue);
  const dayOfMonthDisplay = isLastDayOfMonthSelected
    ? i18n('workflowTemplate.component.dateAndRepeat.lastDayOfMonth')
    : i18n('workflowTemplate.component.dateAndRepeat.nthDayOfMonth', {
        nth: dayOfMonthStr,
      });

  const [weekday, setWeekday] = useState(DaysOfWeek[dstartDate.getUTCDay()]);
  const [nthWeekdayOfMonth, setNthWeekdayOfMonth] = useState(getNthWeekdayOfMonth(dstartDate));
  const fallbackNthWeekdayDisplay = weekday.nth(nthWeekdayOfMonth);
  const nthDayToUse =
    isNthWeekdaySelected && !isNil(rruleOptions) && !isNil(rruleOptions?.byweekday)
      ? rruleOptions.byweekday[0]
      : fallbackNthWeekdayDisplay;
  const nthDayDisplay =
    nthDayToUse.n === -1
      ? i18n('workflowTemplate.component.dateAndRepeat.last')
      : moment.localeData().ordinal(nthDayToUse.n);
  const nthDayWeekdayDisplay = dayOfWeekLabels[nthDayToUse.weekday];

  useEffect(() => {
    if (!isDayOfMonthSelected && !isNthWeekdaySelected) {
      setRruleOptions(prev => buildCustomRRule(prev, { bymonthday: dstartDate.getUTCDate() }));
    }
  }, [isDayOfMonthSelected, isNthWeekdaySelected, setRruleOptions, dstartDate]);

  const handleDateSelect = (day: number) => {
    setDateNumberValue(day);
    setRruleOptions(prev => {
      return buildCustomRRule(
        prev,
        {
          bymonthday: day,
        },
        true,
      );
    });
  };

  const handleDayOfMonthSelect = () => {
    handleDateSelect(dateNumberValue);
  };

  const handleNthSelect = (nth: number) => {
    setNthWeekdayOfMonth(nth);
    setRruleOptions(prev => {
      const existingWeekday = rruleOptions?.byweekday?.[0]?.weekday;
      const existingWeekdayObj = !isNil(existingWeekday) ? DaysOfWeek[existingWeekday] : weekday;

      return buildCustomRRule(
        prev,
        {
          byweekday: [existingWeekdayObj.nth(nth)],
        },
        true,
      );
    });
  };

  const handleDaySelect = (dayOfWeek: Weekday) => {
    setWeekday(dayOfWeek);
    const existingNth =
      isNthWeekdaySelected && !isNil(rruleOptions) && !isNil(rruleOptions?.byweekday)
        ? rruleOptions.byweekday[0].n
        : nthWeekdayOfMonth;
    setRruleOptions(prev => {
      return buildCustomRRule(
        prev,
        {
          byweekday: [dayOfWeek.nth(existingNth)],
        },
        true,
      );
    });
  };

  const handleFullNthSelect = () => {
    setRruleOptions(prev =>
      buildCustomRRule(prev, { byweekday: [weekday.nth(nthWeekdayOfMonth)] }, true),
    );
  };

  const dateSelectOptions: { title: string; onPress: () => void }[] = [];
  for (let i = 1; i <= 31; i++) {
    dateSelectOptions.push({
      title: moment.localeData().ordinal(i),
      onPress: () => handleDateSelect(i),
    });
  }
  dateSelectOptions.push({
    title: i18n('workflowTemplate.component.dateAndRepeat.lastDayOfMonth'),
    onPress: () => handleDateSelect(-1),
  });

  const nthSelectOptions: { title: string; onPress: () => void }[] = [];
  for (let i = 1; i <= 4; i++) {
    nthSelectOptions.push({
      title: moment.localeData().ordinal(i),
      onPress: () => handleNthSelect(i),
    });
  }
  nthSelectOptions.push({
    title: i18n('workflowTemplate.component.dateAndRepeat.last'),
    onPress: () => handleNthSelect(-1),
  });

  const daySelectOptions: { title: string; onPress: () => void }[] = [];
  DaysOfWeek.forEach(day => {
    daySelectOptions.push({
      title: dayOfWeekLabels[day.weekday],
      onPress: () => handleDaySelect(day),
    });
  });

  return {
    dateSelectOptions,
    nthSelectOptions,
    daySelectOptions,

    handleDayOfMonthSelect,
    handleFullNthSelect,

    isDayOfMonthSelected,
    isNthWeekdaySelected,

    dayOfMonthDisplay,
    nthDayDisplay,
    nthDayWeekdayDisplay,
  };
}
